import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { S12nSessionDatesQuery } from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramGraphqlQueries';
import type {
  S12nSessionDatesQuery as S12nSessionDatesQueryData,
  S12nSessionDatesQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/__generated__/S12nSessionDatesQuery';
import type { EnterpriseProductMetadataConfiguration } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import { useCuratedCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseCuratedCollectionsContext';
import { useForCreditCollection } from 'bundles/page-config-common/contexts/enterprise/EnterpriseForCreditCollectionContext';
import EnterpriseSpecializationSessionsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseSpecializationSessionsContext';

type Props = {
  children: React.ReactNode;
};

const getS12nSessionIds = (productMetadata?: EnterpriseProductMetadataConfiguration) =>
  productMetadata
    ?.filter((metadata) => metadata?.metadataType?.specialization?.associatedSessionId)
    .map((metadata) => metadata?.metadataType?.specialization?.associatedSessionId as string) || [];

// Program Curriculum Resource does not return the session dates for the specialization sessions.
// Thus, we need to call this query to get the dates for the specialization sessions.
const EnterpriseSpecializationSessionsProvider = ({ children }: Props) => {
  const { forCreditCollection, forCreditCollectionLoading } = useForCreditCollection();
  const { curatedCollections, curatedCollectionsLoading } = useCuratedCollections();

  const forCreditProductMetadata = forCreditCollection?.enterpriseProductConfigurations;
  const curatedCollectionsProductMetadata =
    curatedCollections?.map((curatedCollection) => curatedCollection?.enterpriseProductConfigurations) || [];

  const allProductMetadata = [forCreditProductMetadata, ...curatedCollectionsProductMetadata]
    .filter(Boolean)
    .flat() as EnterpriseProductMetadataConfiguration;

  const s12nSessionIds = useMemo(() => getS12nSessionIds(allProductMetadata), [allProductMetadata]);
  const uniqueS12nSessionIds = useMemo(() => [...new Set(s12nSessionIds)], [s12nSessionIds]);
  const collectionsLoading = forCreditCollectionLoading || curatedCollectionsLoading;

  const { data, loading } = useQuery<S12nSessionDatesQueryData, S12nSessionDatesQueryVariables>(S12nSessionDatesQuery, {
    variables: {
      ids: uniqueS12nSessionIds,
    },
    skip: collectionsLoading || !uniqueS12nSessionIds.length,
    ssr: false,
    errorPolicy: 'all' as const,
  });

  if (collectionsLoading || loading) {
    return (
      <EnterpriseSpecializationSessionsContext.Provider value={{ s12nSessionsLoading: true, s12nSessions: undefined }}>
        {children}
      </EnterpriseSpecializationSessionsContext.Provider>
    );
  }

  const s12nSessions = filterExistsOrDefault(data?.OnDemandCourseSessionsV1Resource?.multiGet?.elements);

  return (
    <EnterpriseSpecializationSessionsContext.Provider value={{ s12nSessionsLoading: false, s12nSessions }}>
      {children}
    </EnterpriseSpecializationSessionsContext.Provider>
  );
};

export default EnterpriseSpecializationSessionsProvider;
